import { Children, cloneElement, type ReactElement, type ReactNode } from "react";
import { View } from "tamagui";
import { SkeletonShimmer } from "./SkeletonShimmer";
const DEFAULT_CHARS = 12;

// The classic text-skeleton proportion (MUI uses scale(1, 0.6)): the bar fills
// part of the line box so it reads as a text line, not a solid block.
const TEXT_BAR_HEIGHT = "60%";
export type SkeletonTextProps = {
  loading?: boolean;
  /** Placeholder width in characters when there's no content yet (first load). */
  chars?: number;
  /** The real text element — its typography sets the skeleton's metrics. */
  children: ReactElement<{
    children?: ReactNode;
    numberOfLines?: number;
  }>;
};

// A skeleton is a loading state of the real text, not a parallel shape: while
// loading, the same element renders invisibly (its own content when present,
// else `chars` of placeholder) so the shimmer inherits the exact font metrics —
// the skeleton's height and line-height can't drift from the loaded state.
// Decorative only (hidden from assistive tech): the region that swaps
// skeleton↔content owns the announcement — set `aria-busy` on that wrapper.
export function SkeletonText({
  loading = false,
  chars = DEFAULT_CHARS,
  children
}: SkeletonTextProps) {
  if (!loading) {
    return children;
  }
  const content = children.props.children;
  // toArray drops values React renders as nothing (null, booleans, []), so
  // conditional children fall back to the placeholder, not a zero-width box.
  const hasContent = Children.toArray(content).some(child => child !== "");
  const sized = hasContent ? children : cloneElement(children, {
    numberOfLines: 1
  }, "X".repeat(chars));
  return <View alignSelf="flex-start" position="relative" maxWidth="100%" overflow="hidden" aria-hidden data-sentry-element="View" data-sentry-component="SkeletonText" data-sentry-source-file="SkeletonText.tsx">
      <View opacity={0} pointerEvents="none" data-sentry-element="View" data-sentry-source-file="SkeletonText.tsx">
        {sized}
      </View>
      <View position="absolute" top={0} left={0} right={0} bottom={0} justifyContent="center" data-sentry-element="View" data-sentry-source-file="SkeletonText.tsx">
        <SkeletonShimmer height={TEXT_BAR_HEIGHT} radius={4} data-sentry-element="SkeletonShimmer" data-sentry-source-file="SkeletonText.tsx" />
      </View>
    </View>;
}